import { Controller } from "@hotwired/stimulus"

import { formatTime } from "@/lib/utils"

// Mirrors the global player's progress on a page-level display for one
// item: the item page's inline player (elapsed / fill bar) and the
// container tracklist's number ring. Listens to
// persistent-player:progress@document and only reacts when the playing
// item matches. Clicking the track dispatches a seek request that the
// global player honors while this item is current.
export default class extends Controller {
  static values = { itemId: String }
  static targets = ["elapsed", "fill", "track", "ring", "duration"]

  declare readonly itemIdValue: string
  declare readonly elapsedTarget: HTMLElement
  declare readonly hasElapsedTarget: boolean
  declare readonly fillTarget: HTMLElement
  declare readonly hasFillTarget: boolean
  declare readonly trackTarget: HTMLElement
  declare readonly hasRingTarget: boolean
  declare readonly ringTarget: HTMLElement
  declare readonly durationTarget: HTMLElement
  declare readonly hasDurationTarget: boolean

  progress(
    event: CustomEvent<{
      itemId: string
      currentTime: number
      duration: number
    }>,
  ) {
    const { itemId, currentTime, duration } = event.detail
    if (itemId !== this.itemIdValue) {
      this.reset()
      return
    }

    const ratio = duration > 0 ? currentTime / duration : 0
    if (this.hasFillTarget) this.fillTarget.style.width = `${ratio * 100}%`
    if (this.hasElapsedTarget)
      this.elapsedTarget.textContent = formatTime(currentTime)
    if (this.hasRingTarget)
      this.ringTarget.setAttribute("value", String(Math.round(ratio * 100)))
    if (this.hasDurationTarget && duration > 0)
      this.durationTarget.textContent = formatTime(duration)
  }

  seek(event: MouseEvent) {
    const rect = this.trackTarget.getBoundingClientRect()
    if (rect.width === 0) return

    const ratio = (event.clientX - rect.left) / rect.width
    this.dispatch("seek", {
      detail: { itemId: this.itemIdValue, ratio },
      prefix: "track-progress",
    })
  }

  private reset() {
    if (this.hasFillTarget) this.fillTarget.style.width = "0%"
    if (this.hasElapsedTarget) this.elapsedTarget.textContent = "0:00"
    if (this.hasRingTarget) this.ringTarget.setAttribute("value", "0")
  }
}
